import React from 'react';
import { Scale, FileSearch, FileText, Search, Briefcase, FlaskConical } from 'lucide-react';

export interface UseCaseDetailRecord {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  benefits: string[];
  useCases: string[];
  industries: string[];
  content: string;
}

const contractReview: UseCaseDetailRecord = {
  id: 'contract-review',
  title: 'Contract Review & Analysis',
  icon: Scale,
  description: 'Contract review is one of the most time-consuming tasks in business. Our AI automates this process, allowing you to quickly identify key terms, risks, obligations, and unusual clauses in any contract.',
  benefits: [
    'Identify key terms and obligations in seconds',
    'Spot unusual or risky clauses automatically',
    'Compare contracts across multiple documents',
    'Extract specific provisions (indemnification, termination, etc.)',
    'Review hundreds of contracts in minutes',
  ],
  useCases: [
    'Commercial and vendor agreement review',
    'Employment and partnership contract analysis',
    'M&A and transaction due diligence',
    'Lease and real estate contract review',
    'Identifying non-compete and NDA clauses',
  ],
  industries: ['Law Firms', 'Corporate Legal', 'Procurement', 'Real Estate', 'HR & Employment'],
  content: `
    <h2>Overview</h2>
    <p>Contract review is one of the most time-consuming tasks in business and legal practice. Whether you're managing vendor agreements, employment contracts, partnership deals, or M&A documentation, identifying key terms, risks, obligations, and unusual clauses manually can take hours per document. Our document AI automates this process so you can focus on negotiation and strategy.</p>
    
    <h2>How It Works</h2>
    <p>Upload your contracts (PDF, Word, or other supported formats) and ask questions in plain language. For example: "What are the termination clauses in this employment contract?" or "Find all indemnification provisions across these contracts." The AI understands legal and commercial terminology and returns precise answers with citations to specific sections and pages.</p>
    
    <p>You can compare versions, spot deviations from your standard templates, and run bulk checks across hundreds of contracts. Results are traceable to source text, so your legal and business teams can verify and act with confidence.</p>
    
    <h2>Key Capabilities</h2>
    <p>Extract payment terms, key dates, liability caps, and renewal provisions. Identify non-compete, non-disclosure, and force majeure clauses. Compare multiple contracts side by side and flag inconsistencies or outlier terms. All of this is possible without sending sensitive contracts to public AI services when you use our private LLM deployment.</p>
    
    <h2>Private LLM Option</h2>
    <p>For confidential or high-value contracts, our private LLM deployment keeps all documents and queries inside your own infrastructure. No data is sent to public clouds, supporting compliance and attorney-client privilege.</p>
  `,
};

const documentAnalysis: UseCaseDetailRecord = {
  id: 'document-analysis',
  title: 'Document Analysis & Review',
  icon: FileSearch,
  description: 'Business operations require analyzing massive amounts of documents—reports, contracts, agreements, and records. Our AI helps you find the information you need instantly and extract key insights.',
  benefits: [
    'Analyze reports and business documents quickly',
    'Extract key information and insights',
    'Identify patterns and trends across documents',
    'Build timelines from document records',
    'Make informed decisions with comprehensive analysis',
  ],
  useCases: [
    'Board and executive report summarization',
    'Financial and audit document analysis',
    'RFP and proposal comparison',
    'Meeting notes and action item extraction',
    'Cross-document trend and pattern discovery',
  ],
  industries: ['Corporate Strategy', 'Finance & Audit', 'Consulting', 'Operations', 'Business Development'],
  content: `
    <h2>Overview</h2>
    <p>Organizations sit on large volumes of reports, contracts, and business documents. Finding the right information, spotting patterns, and building a clear picture often requires manual reading and note-taking. Our document AI turns your corpus into a queryable knowledge base so you can get answers and summaries in seconds.</p>
    
    <h2>How It Works</h2>
    <p>Upload or connect your documents (including from cloud storage or internal repositories). Ask questions such as: "What are the main findings in these reports?" or "Extract all financial information from these documents." The AI analyzes content across files, summarizes themes, and cites sources so you can drill down when needed.</p>
    
    <h2>Key Capabilities</h2>
    <p>Summarize long reports, compare multiple documents, extract dates and figures, and identify recurring themes or risks. Use the same platform for ad-hoc research and for recurring workflows like quarterly report reviews or deal analysis.</p>
    
    <h2>Private LLM Option</h2>
    <p>When documents contain confidential or proprietary data, run the AI on your own infrastructure with our private LLM deployment. Data never leaves your environment.</p>
  `,
};

const dueDiligence: UseCaseDetailRecord = {
  id: 'due-diligence',
  title: 'Due Diligence',
  icon: FileText,
  description: 'Due diligence requires reviewing hundreds or thousands of documents to identify risks, liabilities, and potential deal-breakers. Our AI accelerates this process so you can complete thorough reviews in days instead of weeks.',
  benefits: [
    'Review hundreds of documents in hours, not weeks',
    'Identify risks and liabilities automatically',
    'Find potential deal-breakers quickly',
    'Extract key financial and legal information',
    'Complete comprehensive due diligence faster',
  ],
  useCases: [
    'M&A and acquisition document review',
    'Real estate and asset transaction diligence',
    'Investment and fund due diligence',
    'Vendor and partner risk assessment',
    'Regulatory and compliance document checks',
  ],
  industries: ['M&A and Corporate Development', 'Private Equity', 'Real Estate', 'Legal', 'Investment Teams'],
  content: `
    <h2>Overview</h2>
    <p>Due diligence for transactions often involves thousands of documents—contracts, financials, regulatory filings, and internal records. Manual review is slow, expensive, and can miss critical issues. Our document AI lets you run targeted questions across the entire data room and get structured answers with citations.</p>
    
    <h2>How It Works</h2>
    <p>Load the data room (or a subset) into the platform and ask questions like: "What are the material liabilities in these documents?" or "Find all contracts with termination clauses" or "Identify any regulatory compliance issues." The AI searches and synthesizes answers, so your team can focus on judgment and negotiation instead of line-by-line reading.</p>
    
    <h2>Key Capabilities</h2>
    <p>Identify material contracts, financial obligations, litigation exposure, and regulatory risks. Compare findings across documents and build a clear picture of deal risks and opportunities. All with full traceability to source documents for audit and legal review.</p>
    
    <h2>Private LLM Option</h2>
    <p>Deal materials are highly sensitive. With a private LLM deployment, all documents and queries stay inside your or your client's environment, supporting confidentiality and compliance.</p>
  `,
};

const intelligentSearch: UseCaseDetailRecord = {
  id: 'intelligent-search',
  title: 'Intelligent Search & Discovery',
  icon: Search,
  description: 'Building on past insights is key to making better decisions. Our AI helps you search through your document library, reports, and research materials to find relevant information and patterns.',
  benefits: [
    'Search your entire document library instantly',
    'Find relevant information and similar documents',
    'Learn from past successful projects',
    'Make better decisions with proven approaches',
    'Maintain a searchable knowledge base',
  ],
  useCases: [
    'Finding documents similar to a given contract or report',
    'Reusing past approaches and playbooks',
    'Pricing and strategy research across historical docs',
    'Building and querying a corporate knowledge base',
  ],
  industries: ['Knowledge Management', 'Strategy', 'Sales & Business Development', 'R&D', 'Operations'],
  content: `
    <h2>Overview</h2>
    <p>Organizations accumulate vast document libraries, but traditional keyword search often misses context and nuance. Our AI understands meaning and context, so you can ask questions in natural language and discover relevant documents, similar cases, and recurring patterns—even when exact phrases differ.</p>
    
    <h2>How It Works</h2>
    <p>Index your documents (reports, contracts, proposals, past projects) and ask: "Find documents similar to this contract" or "What approaches were successful in similar projects?" or "Search for information on pricing strategies." The system returns relevant passages and documents with explanations, helping you reuse knowledge and avoid reinventing the wheel.</p>
    
    <h2>Key Capabilities</h2>
    <p>Semantic search goes beyond keywords to match intent and concepts. You can combine with filters (date, author, type) and use the same platform for one-off research and for maintaining a living knowledge base that new team members can query.</p>
    
    <h2>Private LLM Option</h2>
    <p>When your knowledge base contains confidential or proprietary content, run search and discovery on a private LLM so nothing is sent to public AI services.</p>
  `,
};

const compliance: UseCaseDetailRecord = {
  id: 'compliance',
  title: 'Compliance & Regulatory Review',
  icon: Briefcase,
  description: 'Staying compliant with ever-changing regulations is challenging. Our AI helps you review regulatory documents, track compliance requirements, and stay ahead of policy changes.',
  benefits: [
    'Review regulatory documents quickly',
    'Track compliance requirements across documents',
    'Stay updated on policy changes',
    'Identify compliance gaps',
    'Ensure organizational adherence to regulations',
  ],
  useCases: [
    'Regulatory and policy change impact analysis',
    'Cross-document compliance requirement tracking',
    'Data privacy and GDPR/CCPA alignment checks',
    'Identifying compliance gaps in contracts and policies',
  ],
  industries: ['Compliance & Legal', 'Financial Services', 'Healthcare', 'Government', 'Risk Management'],
  content: `
    <h2>Overview</h2>
    <p>Regulations and internal policies change frequently. Tracking what applies to your organization and where your documents and processes align—or fall short—is difficult at scale. Our document AI helps you review regulatory texts, map requirements to your documents, and identify gaps or needed updates.</p>
    
    <h2>How It Works</h2>
    <p>Upload regulations, policies, and your own contracts or procedures. Ask questions like: "What are the compliance requirements in these regulations?" or "Find all mentions of data privacy requirements" or "What policy changes affect this client?" The AI surfaces relevant provisions and can help you trace obligations across many documents.</p>
    
    <h2>Key Capabilities</h2>
    <p>Monitor regulatory and policy updates, compare document sets for consistency, and maintain an auditable view of how your organization meets (or fails to meet) specific requirements. Citations to source text support audits and remediation planning.</p>
    
    <h2>Private LLM Option</h2>
    <p>Compliance documents and internal assessments are sensitive. A private LLM keeps all processing on your infrastructure, supporting data residency and confidentiality requirements.</p>
  `,
};

const policyReview: UseCaseDetailRecord = {
  id: 'policy-review',
  title: 'Policy & Compliance Review',
  icon: FileText,
  description: 'Policy and compliance review requires careful examination of policies, compliance documents, and regulatory filings to ensure they meet standards and avoid inconsistencies.',
  benefits: [
    'Review policy documents thoroughly',
    'Identify inconsistencies and potential issues',
    'Verify provisions align with organizational requirements',
    'Ensure compliance with regulations',
    'Maintain standards with precision and care',
  ],
  useCases: [
    'Policy document review and gap analysis',
    'Consistency checks across compliance documents',
    'Verifying alignment with organizational standards',
    'Finding mentions of specific regulations',
  ],
  industries: ['Compliance', 'Legal', 'HR', 'Quality & Standards', 'Internal Audit'],
  content: `
    <h2>Overview</h2>
    <p>Policies, procedures, and compliance documents must be accurate, consistent, and aligned with both internal standards and external regulations. Manual review is slow and can miss inconsistencies. Our AI helps you review at scale and verify that key provisions are present and aligned across documents.</p>
    
    <h2>How It Works</h2>
    <p>Upload policy documents, compliance manuals, and related filings. Ask: "What are the key provisions in this policy?" or "Identify any inconsistencies in the compliance documents" or "Find all mentions of specific regulations." The system highlights relevant sections and can compare across documents to spot conflicts or gaps.</p>
    
    <h2>Key Capabilities</h2>
    <p>Summarize long policy documents, extract obligations and rights, and check for consistency with other policies or regulations. Use the same workflow for annual policy reviews and for ad-hoc checks when new regulations or standards are introduced.</p>
    
    <h2>Private LLM Option</h2>
    <p>Internal policies and compliance assessments often contain confidential information. Run reviews on a private LLM so data stays within your environment.</p>
  `,
};

const scientificResearch: UseCaseDetailRecord = {
  id: 'scientific-research',
  title: 'Research & Analysis for Scientific Users',
  icon: FlaskConical,
  description: 'Scientific research generates vast amounts of papers, lab notes, and datasets. Our document AI with private LLM deployment lets you query literature and surface patterns across your corpus without sending data to public clouds—ideal for labs, pharma, and academia.',
  benefits: [
    'Run AI on papers and lab notes inside your own environment',
    'Literature review and citation discovery at scale',
    'Reproducibility and audit trails with private LLM',
    'No sensitive data leaves your infrastructure',
    'Compliance with research ethics and data governance',
  ],
  useCases: [
    'Literature review and systematic review support',
    'Finding similar methodology or results across papers',
    'Summarizing trial reports and lab notes',
    'Compound, mechanism, and side-effect discovery in text',
  ],
  industries: ['Academic Research', 'Pharma R&D', 'Biotech', 'Clinical Research', 'Labs & CROs'],
  content: `
    <h2>Overview</h2>
    <p>Researchers and scientific organizations work with large corpora of papers, pre-prints, lab notebooks, and trial reports. Manual literature review and cross-paper analysis are time-consuming and can miss important connections. Our document AI helps you query this content in natural language and get answers with citations—while keeping sensitive or pre-publication data on your own infrastructure with a private LLM.</p>
    
    <h2>Why Private LLM for Science</h2>
    <p>Scientific data often includes confidential trial data, unpublished results, proprietary methods, or human subject information. Sending such content to public AI services can violate data governance, funder requirements, and research ethics. Our private LLM deployment runs the same document AI capabilities entirely within your environment: data never leaves your infrastructure, and you retain full control over access, retention, and audit trails. This supports reproducibility, compliance, and collaboration with partners who require data sovereignty.</p>
    
    <h2>How It Works</h2>
    <p>Upload or connect your papers, reports, and lab notes. Ask questions like: "Which papers in our library report similar methodology?" or "Summarize the main conclusions from these trial reports" or "Find all mentions of this compound and side effects." The AI searches across your corpus and returns answers with source citations. You can compare findings, spot conflicting results, and build a clear picture without manually reading hundreds of documents.</p>
    
    <h2>Key Capabilities</h2>
    <p>Literature review and citation discovery, summarization of long papers or report sets, extraction of methods and outcomes, and identification of gaps or conflicts in the literature. All processing can run on your own servers or approved cloud with a private LLM, so you meet data residency and ethics requirements while still benefiting from AI-powered analysis.</p>
    
    <h2>Ideal For</h2>
    <p>Academic institutions, pharma and biotech R&D, clinical research organizations, and any team that needs to combine powerful document AI with strict control over where data is stored and processed. Private LLM is the recommended deployment for scientific use cases.</p>
  `,
};

export const useCaseDetails: Record<string, UseCaseDetailRecord> = {
  'contract-review': contractReview,
  'document-analysis': documentAnalysis,
  'due-diligence': dueDiligence,
  'intelligent-search': intelligentSearch,
  'compliance': compliance,
  'policy-review': policyReview,
  'compliance-review': policyReview,
  'scientific-research': scientificResearch,
};
